/**
 * Menu déroulant ou contextuel (design system, Menu) : flèches, Origine, Fin, première
 * lettre, Entrée ou Espace pour choisir, Échap pour fermer ; le focus revient au
 * déclencheur. Rendu par-dessus la page, placé contre son ancre.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../outils';
import { Icone, type NomIcone } from './Icone';
import { raccourciAria } from './Touche';

export type MenuElement =
  | {
      type: 'action';
      libelle: string;
      icone?: NomIcone;
      raccourci?: string;
      danger?: boolean;
      desactive?: boolean;
      description?: string;
      onChoisir: () => void;
    }
  | { type: 'case'; libelle: string; coche: boolean; onBasculer: (coche: boolean) => void }
  | { type: 'sous-menu'; libelle: string; icone?: NomIcone; elements: MenuElement[] }
  | { type: 'separateur' }
  | { type: 'titre'; libelle: string };

export type Ancre = HTMLElement | { x: number; y: number };

export interface MenuProps {
  elements: MenuElement[];
  ancre: Ancre;
  /** Aligne le menu sur le bord droit de l'ancre. */
  aDroite?: boolean;
  etiquette?: string;
  onFermer: () => void;
}

function elementsFocalisables(menu: HTMLElement | null): HTMLButtonElement[] {
  if (menu === null) return [];
  return [
    ...menu.querySelectorAll<HTMLButtonElement>(
      ':scope > .fc-menu__element, :scope > .fc-menu__sous-menu > .fc-menu__element',
    ),
  ];
}

function Liste({
  elements,
  onFermer,
  fermerTout,
  etiquette,
  style,
  sousMenu = false,
}: {
  elements: MenuElement[];
  onFermer: () => void;
  fermerTout: () => void;
  etiquette?: string;
  style?: React.CSSProperties;
  sousMenu?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [ouvert, setOuvert] = useState<number | null>(null);

  useEffect(() => {
    elementsFocalisables(ref.current)[0]?.focus();
  }, []);

  function clavier(e: KeyboardEvent<HTMLDivElement>) {
    if (e.target instanceof HTMLElement && e.target.closest('.fc-menu') !== ref.current) return;
    const liste = elementsFocalisables(ref.current);
    const i = liste.indexOf(document.activeElement as HTMLButtonElement);
    const aller = (j: number) => liste[(j + liste.length) % liste.length]?.focus();
    switch (e.key) {
      case 'ArrowDown':
        aller(i + 1);
        break;
      case 'ArrowUp':
        aller(i - 1);
        break;
      case 'Home':
        aller(0);
        break;
      case 'End':
        aller(liste.length - 1);
        break;
      case 'Escape':
      case 'ArrowLeft':
        if (e.key === 'ArrowLeft' && !sousMenu) return;
        onFermer();
        break;
      case 'Tab':
        fermerTout();
        return;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const lettre = e.key.toLocaleLowerCase('fr');
          const suivants = [...liste.slice(i + 1), ...liste.slice(0, i + 1)];
          suivants
            .find((b) => b.textContent?.trim().toLocaleLowerCase('fr').startsWith(lettre))
            ?.focus();
          break;
        }
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <div
      ref={ref}
      className="fc-menu"
      role="menu"
      aria-label={etiquette}
      style={style}
      onKeyDown={clavier}
    >
      {elements.map((element, index) => {
        switch (element.type) {
          case 'separateur':
            return <div key={index} className="fc-menu__separateur" role="separator" />;
          case 'titre':
            return (
              <div key={index} className="fc-menu__titre" role="presentation">
                {element.libelle}
              </div>
            );
          case 'case':
            return (
              <button
                key={index}
                type="button"
                className="fc-menu__element"
                role="menuitemcheckbox"
                aria-checked={element.coche}
                tabIndex={-1}
                onMouseEnter={(e) => e.currentTarget.focus()}
                onClick={() => element.onBasculer(!element.coche)}
              >
                <span className="fc-menu__libelle">{element.libelle}</span>
                <Icone nom="check" className="fc-menu__coche" />
              </button>
            );
          case 'sous-menu':
            return (
              <div key={index} className="fc-menu__sous-menu" role="none">
                <button
                  type="button"
                  className="fc-menu__element"
                  role="menuitem"
                  tabIndex={-1}
                  aria-haspopup="menu"
                  aria-expanded={ouvert === index}
                  onMouseEnter={(e) => {
                    e.currentTarget.focus();
                    setOuvert(index);
                  }}
                  onClick={() => setOuvert(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      setOuvert(index);
                    }
                  }}
                >
                  {element.icone && <Icone nom={element.icone} className="fc-menu__icone" />}
                  <span className="fc-menu__libelle">{element.libelle}</span>
                  <Icone nom="chevron-right" className="fc-menu__chevron" />
                </button>
                {ouvert === index && (
                  <Liste
                    elements={element.elements}
                    sousMenu
                    fermerTout={fermerTout}
                    onFermer={() => {
                      setOuvert(null);
                      elementsFocalisables(ref.current)
                        .find((b) => b.getAttribute('aria-expanded') !== null)
                        ?.focus();
                    }}
                  />
                )}
              </div>
            );
          case 'action':
            return (
              <button
                key={index}
                type="button"
                className={cx('fc-menu__element', element.danger && 'fc-menu__element--danger')}
                role="menuitem"
                tabIndex={-1}
                aria-disabled={element.desactive || undefined}
                aria-keyshortcuts={element.raccourci ? raccourciAria(element.raccourci) : undefined}
                onMouseEnter={(e) => {
                  e.currentTarget.focus();
                  setOuvert(null);
                }}
                onClick={() => {
                  if (element.desactive) return;
                  fermerTout();
                  element.onChoisir();
                }}
              >
                {element.icone && <Icone nom={element.icone} className="fc-menu__icone" />}
                {element.description ? (
                  <span className="fc-menu__texte">
                    <span className="fc-menu__libelle">{element.libelle}</span>
                    <span className="fc-menu__description">{element.description}</span>
                  </span>
                ) : (
                  <span className="fc-menu__libelle">{element.libelle}</span>
                )}
                {element.raccourci && (
                  <kbd className="fc-touche fc-menu__raccourci" aria-hidden="true">
                    {element.raccourci}
                  </kbd>
                )}
              </button>
            );
        }
      })}
    </div>
  );
}

export function Menu({ elements, ancre, aDroite = false, etiquette, onFermer }: MenuProps) {
  const conteneur = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const declencheur = useRef<HTMLElement | null>(
    ancre instanceof HTMLElement ? ancre : (document.activeElement as HTMLElement | null),
  );

  useLayoutEffect(() => {
    const menu = conteneur.current?.firstElementChild as HTMLElement | null;
    const largeur = menu?.offsetWidth ?? 200;
    const hauteur = menu?.offsetHeight ?? 0;
    let top: number;
    let left: number;
    if (ancre instanceof HTMLElement) {
      const r = ancre.getBoundingClientRect();
      top = r.bottom + 4;
      left = aDroite ? r.right - largeur : r.left;
      if (top + hauteur > window.innerHeight - 8) top = Math.max(8, r.top - 4 - hauteur);
    } else {
      top = ancre.y;
      left = ancre.x;
      if (top + hauteur > window.innerHeight - 8) top = Math.max(8, ancre.y - hauteur);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - largeur - 8));
    setPosition({ top, left });
  }, [ancre, aDroite]);

  const fermer = () => {
    onFermer();
    declencheur.current?.focus();
  };

  useEffect(() => {
    const exterieur = (e: PointerEvent) => {
      if (!conteneur.current?.contains(e.target as Node) && e.target !== declencheur.current) {
        onFermer();
      }
    };
    document.addEventListener('pointerdown', exterieur, true);
    return () => document.removeEventListener('pointerdown', exterieur, true);
  }, [onFermer]);

  return createPortal(
    <div
      ref={conteneur}
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        zIndex: 'var(--plan-menu)' as unknown as number,
      }}
    >
      <Liste
        elements={elements}
        onFermer={fermer}
        fermerTout={onFermer}
        {...(etiquette ? { etiquette } : {})}
        style={{ ['--origine' as string]: aDroite ? 'top right' : 'top left' }}
      />
    </div>,
    document.body,
  );
}

/** État d'un menu attaché à un bouton : ouvrir, fermer, et propriétés du déclencheur. */
export function useMenu() {
  const [ancre, setAncre] = useState<Ancre | null>(null);
  return {
    ancre,
    ouvert: ancre !== null,
    ouvrir: (a: Ancre) => setAncre(a),
    fermer: () => setAncre(null),
  };
}

export type { ReactNode };
